import type { AccessControlEntryModel, AcePermission, ObjectPermissionsModel } from 'dynamo-wrapper';
import { useTenant } from 'queries/hooks/useTenant';
import { useQuery, UseQueryOptions } from 'react-query';
import API from './API';
import { ProjectedTenantResponse } from './TenantService';
import { handleError, handleResponse } from './util';

export const GetEntityACL = async (entityId: string): Promise<AccessControlEntryModel[]> => {
    return API.get<AccessControlEntryModel[]>(`/accesscontrol/acl/${entityId}`).then(handleResponse).catch(handleError);
};

export const GetEntityPermissions = async (entityId: string): Promise<AcePermission[]> => {
    return API.get<AcePermission[]>(`/accesscontrol/permissions/${entityId}`).then(handleResponse).catch(handleError);
};

export const GetEntityTypePermissions = async (entityType: string): Promise<ObjectPermissionsModel[]> => {
    return API.get<ObjectPermissionsModel[]>(`/accesscontrol/permissions/type/${entityType}`)
        .then(handleResponse)
        .catch(handleError);
};

export const CanWriteToEntity = async (entityId: string): Promise<boolean> => {
    return (await GetEntityPermissions(entityId)).some((callerPermissions) => ['RW', 'AD'].includes(callerPermissions));
};

export const CanCreateWorkspace = async (tenantId: string): Promise<boolean> => {
    return (await GetEntityPermissions(tenantId)).some((callerPermissions) =>
        ['AD', 'canCreateWorkspace'].includes(callerPermissions)
    );
};

export const CanCreateDataSource = async (tenantId: string): Promise<boolean> => {
    return (await GetEntityPermissions(tenantId)).some((callerPermissions) =>
        ['AD', 'canCreateDataSource'].includes(callerPermissions)
    );
};

export const CanManageKpiTypes = async (tenantId: string): Promise<boolean> => {
    return (await GetEntityPermissions(tenantId)).some((callerPermissions) =>
        ['AD', 'canManageKpiTypes'].includes(callerPermissions)
    );
};

export const CanManageNotificationDestinations = async (tenantId: string): Promise<boolean> => {
    return (await GetEntityPermissions(tenantId)).some((callerPermissions) =>
        ['AD', 'canManageNotificationDestinations'].includes(callerPermissions)
    );
};

export const CanManageCustomDataStreams = async (tenantId: string): Promise<boolean> => {
    return (await GetEntityPermissions(tenantId)).some((callerPermissions) =>
        ['AD', 'canManageCustomDataStreams'].includes(callerPermissions)
    );
};

export const CanManageScripts = async (tenantId: string): Promise<boolean> => {
    return (await GetEntityPermissions(tenantId)).some((callerPermissions) =>
        ['AD', 'canManageScripts'].includes(callerPermissions)
    );
};

export const IsTenantAdmin = (tenant: ProjectedTenantResponse | undefined) => tenant?.callerPermissions?.includes('AD');

// Note this is separate from TENANT_USER_GROUPS_QUERY_KEY because TENANT_USER_GROUPS_QUERY_KEY includes
// group members, where this is just the list of groups.
export const ACL_USER_GROUPS_QUERY_KEY = 'ACL_USER_GROUPS_QUERY_KEY';

export const AccessControlQueryKeys = {
    EntityACL: (entityId?: string) => ['ENTITY_ACL_QUERY_KEY', entityId],
    CallerPermissionsOnEntity: (entityId?: string) => ['CALLER_ENTITY_PERMISSIONS_QUERY_KEY', entityId],
    CallerCanWriteToEntity: (entityId?: string) => ['CALLER_CAN_WRITE_TO_ENTITY_QUERY_KEY', entityId],
    CallerCanCreateWorkspace: (entityId?: string) => ['CALLER_CAN_CREATE_WORKSPACE_QUERY_KEY', entityId],
    CallerCanCreateDataSource: (entityId?: string) => ['CALLER_CAN_CREATE_DATA_SOURCE_QUERY_KEY', entityId],
    CallerCanManageKpiTypes: (entityId?: string) => ['CALLER_CAN_MANAGE_KPI_TYPES_QUERY_KEY', entityId],
    CallerCanManageNotificationDestinations: (entityId?: string) => [
        'CALLER_CAN_MANAGE_NOTIFICATION_DESTINATIONS_QUERY_KEY',
        entityId
    ],
    CallerCanManageCustomDataStreams: (entityId?: string) => [
        'CALLER_CAN_MANAGE_CUSTOM_DATA_STREAMS_QUERY_KEY',
        entityId
    ],
    CallerCanManageScripts: (entityId?: string) => ['CALLER_CAN_MANAGE_SCRIPTS_QUERY_KEY', entityId],
    EntityTypePermissions: (entityType: string) => ['ENTITY_TYPE_PERMISSIONS_QUERY_KEY', entityType],
    UserGroups: ACL_USER_GROUPS_QUERY_KEY,

    // 'Lazy update' means the data is cached for a long time but is updated asynchronously
    // on component mount. So it's always available for immediate display, but may show stale date briefly.
    // Designed for use with the ACL editor.
    UserGroupsWithLazyUpdate: [ACL_USER_GROUPS_QUERY_KEY, 'LAZY_UPDATE'],
    UsersWithLazyUpdate: ['ACL_USERS_LAZY_UPDATE']
};

/**
 * Simple wrapper hook to determine if user can write to a specific workspace.
 * Returns the same object as useQuery (data, isLoading etc)
 **/
export const useWorkspaceCanWrite = (
    workspaceId: string | undefined,
    options?: UseQueryOptions<boolean, unknown, boolean, (string | undefined)[]>
) => {
    return useQuery(AccessControlQueryKeys.CallerCanWriteToEntity(workspaceId), () => CanWriteToEntity(workspaceId!), {
        enabled: Boolean(workspaceId),
        cacheTime: Number.POSITIVE_INFINITY,
        staleTime: Number.POSITIVE_INFINITY,
        ...options
    });
};

export const useCanCreateWorkspace = (
    tenantId: string,
    options?: UseQueryOptions<boolean, unknown, boolean, (string | undefined)[]>
) => {
    const tenant = useTenant();
    const isTenantAdmin = Boolean(IsTenantAdmin(tenant.data));
    const queryKey = AccessControlQueryKeys.CallerCanCreateWorkspace(tenantId);
    const canCreateWorkspace = useQuery(queryKey, () => CanCreateWorkspace(tenantId), {
        enabled: Boolean(tenantId),
        cacheTime: Number.POSITIVE_INFINITY,
        staleTime: Number.POSITIVE_INFINITY,
        ...options
    });
    return Boolean(isTenantAdmin || canCreateWorkspace.data);
};

export const useCanCreateDataSource = (
    tenantId: string,
    options?: UseQueryOptions<boolean, unknown, boolean, (string | undefined)[]>
) => {
    const tenant = useTenant();
    const isTenantAdmin = Boolean(IsTenantAdmin(tenant.data));
    const queryKey = AccessControlQueryKeys.CallerCanCreateDataSource(tenantId);
    const canCreateDataSource = useQuery(queryKey, () => CanCreateDataSource(tenantId), {
        enabled: Boolean(tenantId),
        cacheTime: Number.POSITIVE_INFINITY,
        staleTime: Number.POSITIVE_INFINITY,
        ...options
    });
    return Boolean(isTenantAdmin || canCreateDataSource.data);
};

export const useCanManageKpiTypes = (
    tenantId: string,
    options?: UseQueryOptions<boolean, unknown, boolean, (string | undefined)[]>
) => {
    const tenant = useTenant();
    const isTenantAdmin = Boolean(IsTenantAdmin(tenant.data));
    const queryKey = AccessControlQueryKeys.CallerCanManageKpiTypes(tenantId);
    const canManageKpiTypes = useQuery(queryKey, () => CanManageKpiTypes(tenantId), {
        enabled: Boolean(tenantId),
        cacheTime: Number.POSITIVE_INFINITY,
        staleTime: Number.POSITIVE_INFINITY,
        ...options
    });
    return Boolean(isTenantAdmin || canManageKpiTypes.data);
};

export const useCanManageNotificationDestinations = (
    tenantId: string,
    options?: UseQueryOptions<boolean, unknown, boolean, (string | undefined)[]>
) => {
    const tenant = useTenant();
    const isTenantAdmin = Boolean(IsTenantAdmin(tenant.data));
    const queryKey = AccessControlQueryKeys.CallerCanManageNotificationDestinations(tenantId);
    const canManageNotificationDestinations = useQuery(queryKey, () => CanManageNotificationDestinations(tenantId), {
        enabled: Boolean(tenantId),
        cacheTime: Number.POSITIVE_INFINITY,
        staleTime: Number.POSITIVE_INFINITY,
        ...options
    });
    return Boolean(isTenantAdmin || canManageNotificationDestinations.data);
};

export const useCanManageCustomDataStreams = (
    tenantId: string,
    options?: UseQueryOptions<boolean, unknown, boolean, (string | undefined)[]>
) => {
    const tenant = useTenant();
    const isTenantAdmin = Boolean(IsTenantAdmin(tenant.data));
    const queryKey = AccessControlQueryKeys.CallerCanManageCustomDataStreams(tenantId);
    const canManageCustomDataStreams = useQuery(queryKey, () => CanManageCustomDataStreams(tenantId), {
        enabled: Boolean(tenantId),
        cacheTime: Number.POSITIVE_INFINITY,
        staleTime: Number.POSITIVE_INFINITY,
        ...options
    });
    return Boolean(isTenantAdmin || canManageCustomDataStreams.data);
};

export const useCanManageScripts = (
    tenantId: string,
    options?: UseQueryOptions<boolean, unknown, boolean, (string | undefined)[]>
) => {
    const tenant = useTenant();
    const isTenantAdmin = Boolean(IsTenantAdmin(tenant.data));
    const queryKey = AccessControlQueryKeys.CallerCanManageScripts(tenantId);
    const canManageScripts = useQuery(queryKey, () => CanManageScripts(tenantId), {
        enabled: Boolean(tenantId),
        cacheTime: Number.POSITIVE_INFINITY,
        staleTime: Number.POSITIVE_INFINITY,
        ...options
    });
    return Boolean(isTenantAdmin || canManageScripts.data);
};
